import type { IconPrimitives, IconProps } from './types';

/**
 * Standard UI icon set — paths extracted from 2026 Kai Styleguide Figma
 * (Streamline-Cyber source library). All glyphs use stroke-based rendering
 * with `stroke={color}` unless noted.
 */
export const buildUtilityIcons = ({ Svg, Path, Circle, Rect, Ellipse, defaultColor }: IconPrimitives) => {
  const AddIcon = ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M11.5 1.5V23.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M22.5 12.5H0.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
    </Svg>
  );

  const BackIcon = ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M19 12H6' stroke={color} strokeWidth='1.25' strokeLinecap='round' strokeLinejoin='round' />
      <Path d='M10 8L6 12L10 16' stroke={color} strokeWidth='1.25' strokeLinecap='round' strokeLinejoin='round' />
    </Svg>
  );

  const BackToTopIcon = ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M11.4997 15.9588L8.33353 19.125L11.4997 22.2912L14.6659 19.125L11.4997 15.9588Z' stroke={color} strokeMiterlimit={10} />
      <Path d='M2 11.2084L11.5 1.70844L21 11.2084' stroke={color} strokeMiterlimit={10} />
    </Svg>
  );

  const MoreMenuIcon = ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M13.5 4.25L11.5 5.5L9.5 4.25V2.25L11.5 1L13.5 2.25V4.25Z' stroke={color} strokeMiterlimit={10} strokeLinecap='round' />
      <Path d='M13.5 13.25L11.5 14.5L9.5 13.25V11.25L11.5 10L13.5 11.25V13.25Z' stroke={color} strokeMiterlimit={10} strokeLinecap='round' />
      <Path d='M13.5 22.25L11.5 23.5L9.5 22.25V20.25L11.5 19L13.5 20.25V22.25Z' stroke={color} strokeMiterlimit={10} strokeLinecap='round' />
    </Svg>
  );

  const DropdownIcon = ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
      <Path d='M7.83337 4L16.3334 12.5L7.83337 21' stroke={color} strokeMiterlimit={10} />
    </Svg>
  );

  const CloudSyncIcon = ({ color = defaultColor, size = 24, ...props }: IconProps) => (
    <Svg width={size} height={size} viewBox='0 0 44 44' fill='none' {...props}>
      <Path
        transform='translate(5 8)'
        d='M28.75 6.25008C28 6.25008 27.1875 6.37508 26.5 6.6876C25.3125 2.75008 21.6248 0 17.5 0C14.375 0 11.5 1.5 9.75 4.1248C9 3.8748 8.25 3.7498 7.5 3.7498C3.3752 3.7498 0 7.1248 0 11.2498C0 15.3748 3.375 18.7498 7.5 18.7498H8.18752C8.50002 23.625 12.5627 27.4998 17.4999 27.4998C22.4371 27.4998 26.4999 23.6248 26.8123 18.7498H28.7498C32.1874 18.7498 34.9998 15.9373 34.9998 12.4998C34.9998 9.06228 32.1875 6.25008 28.75 6.25008ZM17.5 26.2501C13 26.2501 9.3752 22.6251 9.3752 18.1253C9.3752 13.6255 13.0002 10.0005 17.5 10.0005C21.9998 10.0005 25.6248 13.6255 25.6248 18.1253C25.6248 22.6251 21.9998 26.2501 17.5 26.2501ZM28.75 17.5001H26.8125C26.5 12.6249 22.4373 8.75008 17.5001 8.75008C12.5629 8.75008 8.50008 12.6251 8.18768 17.5001H7.50016C4.06264 17.5001 1.25016 14.6876 1.25016 11.2501C1.25016 7.81256 4.06264 5.00008 7.50016 5.00008C8.25016 5.00008 9.06264 5.12508 9.81264 5.4376C10.1251 5.5626 10.4376 5.4376 10.5626 5.1876C12.0626 2.75008 14.625 1.25008 17.5002 1.25008C21.3127 1.25008 24.6878 3.9376 25.4378 7.68768C25.5003 7.87518 25.6253 8.06268 25.8128 8.1252C26.0003 8.18772 26.1878 8.1877 26.3753 8.1252C27.1253 7.7502 27.9378 7.5002 28.7503 7.5002C31.5003 7.5002 33.7503 9.7502 33.7503 12.5002C33.7503 15.2502 31.5 17.5001 28.75 17.5001Z'
        fill={color}
      />
      <Path
        transform='translate(5 8)'
        d='M14.8259 17.2672C14.6241 17.0654 14.3213 17.0654 14.1195 17.2672L14.0691 17.3177C14.4223 15.7535 15.835 14.593 17.5001 14.593C18.5597 14.593 19.5688 15.0976 20.2752 15.9049C20.4266 16.1067 20.7798 16.1572 20.9816 16.0058C21.1834 15.8545 21.2339 15.5013 21.0825 15.2994C20.2248 14.2399 18.9129 13.5839 17.5504 13.5839C15.2799 13.5839 13.3624 15.249 13.0599 17.469L12.858 17.2672C12.6562 17.0653 12.3535 17.0653 12.1516 17.2672C11.9498 17.469 11.9498 17.7717 12.1516 17.9735L13.1608 18.9827C13.2617 19.0836 13.4131 19.134 13.514 19.134C13.6149 19.134 13.7663 19.0836 13.8672 18.9827L14.8763 17.9735C15.0277 17.7717 15.0277 17.4691 14.8259 17.2672Z'
        fill={color}
      />
      <Path
        transform='translate(5 8)'
        d='M21.1835 17.2671L20.1743 18.2763C19.9725 18.4781 19.9725 18.7808 20.1743 18.9827C20.3762 19.1845 20.6789 19.1845 20.8807 18.9827L20.9312 18.9322C20.578 20.4964 19.1652 21.6569 17.5002 21.6569C16.4406 21.6569 15.4315 21.1523 14.725 20.345C14.5737 20.1432 14.2205 20.0927 14.0186 20.2441C13.8168 20.3954 13.7664 20.7486 13.9177 20.9505C14.7755 22.01 16.0874 22.666 17.4498 22.666C19.7204 22.666 21.6378 21.0009 21.9404 18.7809L22.1422 18.9827C22.2431 19.0837 22.3945 19.1341 22.4954 19.1341C22.5963 19.1341 22.7477 19.0837 22.8486 18.9827C23.0505 18.7809 23.0505 18.4782 22.8486 18.2764L21.8395 17.2672C21.6881 17.0654 21.3853 17.0653 21.1835 17.2671Z'
        fill={color}
      />
    </Svg>
  );

  return {
    // ── Streamline-Cyber icon set (2026 Kai Styleguide) ────────────────────
    SearchIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 17.618 22.7236' fill='none' {...props}>
        <Path d='M4.22567 15.1666L0.559017 7.83331L4.22567 0.5H13.3923L17.059 7.83331L13.3923 15.1666H4.22567Z' stroke={color} strokeMiterlimit={10} />
        <Path d='M17.0589 22.5L13.3923 15.1667' stroke={color} strokeMiterlimit={10} />
      </Svg>
    ),
    FilterIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M19.5 3.5L11.5 13V23.5L8.5 21.5V13L0.5 3.5V1.5H19.5V3.5Z' stroke={color} strokeMiterlimit={10} />
      </Svg>
    ),
    ListIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M9.6 5.1H22M9.6 12H22M9.6 18.22' stroke={color} strokeLinejoin='round' />
        <Rect x='2' y='3.5' width='3' height='3' fill={color} />
        <Rect x='2' y='10.5' width='3' height='3' fill={color} />
        <Rect x='2' y='17.5' width='3' height='3' fill={color} />
      </Svg>
    ),
    AddIcon,
    PlusIcon: AddIcon,
    MinusIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M4 12H20' stroke={color} strokeWidth='0.875' strokeLinecap='round' strokeLinejoin='round' />
      </Svg>
    ),
    CloseIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M0.5 1.5L22.5 23.5' stroke={color} strokeMiterlimit={10} />
        <Path d='M22.5 1.5L0.5 23.5' stroke={color} strokeMiterlimit={10} />
      </Svg>
    ),
    BackIcon,
    BackArrowIcon: BackIcon,
    BackToTopIcon,
    UpArrowIcon: BackToTopIcon,
    DownArrowIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M17.5 18.5L12.5 23.5L7.5 18.5' stroke={color} strokeMiterlimit={10} />
        <Path d='M12.5 23.5V0.5' stroke={color} strokeMiterlimit={10} />
      </Svg>
    ),
    LeftArrowIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M5.5 7.5L0.5 12.5L5.5 17.5' stroke={color} strokeMiterlimit={10} />
        <Path d='M0.5 12.5H23.5' stroke={color} strokeMiterlimit={10} />
      </Svg>
    ),
    RightArrowIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M18.5 7.5L23.5 12.5L18.5 17.5' stroke={color} strokeMiterlimit={10} />
        <Path d='M23.5 12.5H0.5' stroke={color} strokeMiterlimit={10} />
      </Svg>
    ),
    NotificationIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 22.1742 22.559' fill='none' {...props}>
        <Path d='M21.0871 19H1.08711L4.08711 15.5V6.5L11.0871 2L18.0871 6.5V15.5L21.0871 19Z' stroke={color} strokeMiterlimit={10} />
        <Path d='M11.0871 2V0' stroke={color} strokeMiterlimit={10} />
        <Path d='M9.08711 19V21L11.0871 22L13.0871 21V19' stroke={color} strokeMiterlimit={10} />
      </Svg>
    ),
    DownloadIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M22.5 18.5V23.5H0.5V18.5' stroke={color} strokeMiterlimit={10} />
        <Path d='M11.5 17.5V1.5' stroke={color} strokeMiterlimit={10} />
        <Path d='M16 13L11.5 17.5L7 13' stroke={color} strokeMiterlimit={10} />
      </Svg>
    ),
    UploadIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M22.5 18.5V23.5H0.5V18.5' stroke={color} strokeMiterlimit={10} />
        <Path d='M11.5 1.5V17.5' stroke={color} strokeMiterlimit={10} />
        <Path d='M7 6L11.5 1.5L16 6' stroke={color} strokeMiterlimit={10} />
      </Svg>
    ),
    UploadFromComputerIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M8 4L11.5 0.5L15 4' stroke={color} strokeMiterlimit={10} />
        <Path d='M11.5 0.5V14.5' stroke={color} strokeMiterlimit={10} />
        <Path d='M11.5 20.5V23.5' stroke={color} strokeMiterlimit={10} />
        <Path d='M7.5 23.5H15.5' stroke={color} strokeMiterlimit={10} />
        <Path d='M8.5 6.5H0.5V20.5H22.5V6.5H14.5' stroke={color} strokeMiterlimit={10} />
      </Svg>
    ),
    TrashIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M22 6.5H2L3.5 3.5H20.5L22 6.5Z' stroke={color} strokeMiterlimit={10} />
        <Path d='M19.5 6.5H4.5L6.5 23.5H17.5L19.5 6.5Z' stroke={color} strokeMiterlimit={10} />
        <Path d='M14.5 1.5H9.5L8.5 3.5H15.5L14.5 1.5Z' stroke={color} strokeMiterlimit={10} />
        <Path d='M9.5 9.5V20.5' stroke={color} strokeMiterlimit={10} />
        <Path d='M14.5 9.5V20.5' stroke={color} strokeMiterlimit={10} />
      </Svg>
    ),
    CollapseCaretIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 32 32' fill='none' {...props}>
        <Path d='M20 8L12 16L20 24V8Z' fill={color} />
      </Svg>
    ),
    UndoIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M2.5 0.5V7.5H9.5' stroke={color} strokeMiterlimit={10} />
        <Path d='M2.5 7.5L12.5 1L22.5 6.75V18.25L13.508 23.42' stroke={color} strokeMiterlimit={10} />
      </Svg>
    ),
    MoreMenuIcon,
    MoreVerticalIcon: MoreMenuIcon,
    DropdownIcon,
    DropdownArrowIcon: DropdownIcon,
    NoteIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M15.5 23.5H0.5V1.5H22.5V16.5L15.5 23.5Z' stroke={color} strokeMiterlimit={10} />
        <Path d='M15.5 23.5V16.5H22.5' stroke={color} strokeMiterlimit={10} />
      </Svg>
    ),
    InfoCircleIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 21 24.1535' fill='none' {...props}>
        <Path d='M20.5 17.8268L10.5 23.5768L0.5 17.8268V6.32676L10.5 0.576764L20.5 6.32676V17.8268Z' stroke={color} strokeMiterlimit={10} />
        <Path d='M8.5 17.5768H12.5' stroke={color} strokeMiterlimit={10} />
        <Path d='M10.5 5.07676L9.333 6.32676L10.5 7.57676L11.667 6.32676L10.5 5.07676Z' stroke={color} strokeMiterlimit={10} />
        <Path d='M8.5 9.57676H10.5V17.5768' stroke={color} strokeMiterlimit={10} />
      </Svg>
    ),
    ErrorIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M21.5 18L11.5 23.5L1.5 18V7L11.5 1.5L21.5 7V18Z' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
        <Path
          d='M17.157 9.67202L14.328 6.84302L11.5 9.67202L8.67199 6.84302L5.84299 9.67202L8.67199 12.5L5.84299 15.328L8.67199 18.157L11.5 15.328L14.328 18.157L17.157 15.328L14.328 12.5L17.157 9.67202Z'
          stroke={color}
          strokeMiterlimit={10}
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </Svg>
    ),
    AlertIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M11.499 17.5002L9.99928 19L11.499 20.4998L12.9988 19L11.499 17.5002Z' stroke={color} strokeMiterlimit={10} strokeLinecap='round' />
        <Path d='M12 15.5H11L10 9.5H13L12 15.5Z' stroke={color} strokeMiterlimit={10} strokeLinecap='round' />
        <Path d='M0.5 23.5H22.5L11.47 1.5L0.5 23.5Z' stroke={color} strokeMiterlimit={10} strokeLinecap='round' />
      </Svg>
    ),
    FlagIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox={'0 0 24 24'} fill={'none'} {...props}>
        <Path d={'M5.076915 3V21'} stroke={color} strokeLinecap={'square'} strokeLinejoin={'round'} />
        <Path
          d={'M18.923085 9.667896L5.076915 14.769212V3.692342L18.923085 9.667896Z'}
          fill={color}
          stroke={color}
          strokeLinecap={'square'}
          strokeLinejoin={'round'}
        />
      </Svg>
    ),
    SortIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path d='M7.5 23.5V8.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
        <Path d='M2.5 18.5L7.5 23.5L12.5 18.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
        <Path d='M15.5 1.5V16.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
        <Path d='M20.5 6.5L15.5 1.5L10.5 6.5' stroke={color} strokeMiterlimit={10} strokeLinecap='round' strokeLinejoin='round' />
      </Svg>
    ),
    CloudSyncIcon,
    UserProfileIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Circle cx={12} cy={8.25} r={3.25} stroke={color} strokeWidth='1.25' />
        <Path
          d='M6 19C6 15.9624 8.46243 13.5 11.5 13.5H12.5C15.5376 13.5 18 15.9624 18 19'
          stroke={color}
          strokeWidth='1.25'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </Svg>
    ),
    LogoutIcon: ({ color = defaultColor, size = 24, ...props }: IconProps) => (
      <Svg width={size} height={size} viewBox='0 0 24 24' fill='none' {...props}>
        <Path
          d='M10.5 4.5C7.18629 4.5 4.5 7.18629 4.5 10.5V13.5C4.5 16.8137 7.18629 19.5 10.5 19.5'
          stroke={color}
          strokeWidth='1.25'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <Path d='M12 12H19' stroke={color} strokeWidth='1.25' strokeLinecap='round' strokeLinejoin='round' />
        <Path d='M15.5 8.5L19 12L15.5 15.5' stroke={color} strokeWidth='1.25' strokeLinecap='round' strokeLinejoin='round' />
      </Svg>
    ),
  };
};
